import React, { useEffect, useRef, useState } from 'react';
import { Box, FormControl, MenuItem, Pagination, Select, Skeleton, Typography } from '@mui/material';
import PageContainer from '../../components/PageContainer';
import { EmptyState, PageHeader, Panel, StatusChip } from '../../components/ui';
import { IStoryViewerStory, PER_PAGE_OPTIONS } from '../../lib/storyViewer';
import { useStoryViewerData, hasActiveFilters } from './useStoryViewerData';
import StoryViewerFilters from './StoryViewerFilters';
import StoryCard from './StoryCard';
import StoryViewerModal from './StoryViewerModal';

const GRID_SX = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: 2,
};

const StoryViewerPage = () => {
    const { filters, stories, total, status, tags, categories, updateFilters, clearFilters } = useStoryViewerData();
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [playingId, setPlayingId] = useState<number | null>(null);
    const topRef = useRef<HTMLDivElement>(null);

    const openStory = openIndex !== null ? stories[openIndex] || null : null;

    useEffect(() => {
        // The scroll container is PageContainer's content div, so window.scrollTo would not work here.
        topRef.current?.scrollIntoView({ block: 'start' });
    }, [filters.page, filters.perPage]);

    const loading = status === 'loading';
    const pageCount = Math.max(1, Math.ceil(total / filters.perPage));

    const openModal = (story: IStoryViewerStory) => {
        const index = stories.findIndex((item) => item.storyId === story.storyId);
        if (index === -1) return;
        setPlayingId(null); // stop any tile that is playing so its audio does not overlap the popup
        setOpenIndex(index);
        setModalOpen(true);
    };
    const closeModal = () => setModalOpen(false);
    const showPrev = () => setOpenIndex((current) => (current !== null && current > 0 ? current - 1 : current));
    const showNext = () => setOpenIndex((current) => (current !== null && current < stories.length - 1 ? current + 1 : current));

    useEffect(() => {
        // A new result set (filters / page change) invalidates the inline playback and the open index.
        setPlayingId(null);
        if (modalOpen) setModalOpen(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stories]);

    return (
        <PageContainer constrained>
            <div ref={topRef} />
            <PageHeader
                eyebrow="Customer Stories"
                title="Story viewer"
                badges={status !== 'idle' ? <StatusChip tone="neutral">{total.toLocaleString('en-US')} {total === 1 ? 'story' : 'stories'}</StatusChip> : undefined}
                subtitle="Published and approved customer stories. Open a story to watch it, or download the video in the best quality available."
            />
            <Panel padding="sm" className="story-viewer-filters">
                <StoryViewerFilters
                    filters={filters}
                    tags={tags}
                    categories={categories}
                    loading={loading}
                    onChange={(patch) => updateFilters(patch)}
                    onClear={clearFilters}
                />
            </Panel>

            <Box sx={{ position: 'relative', mt: 2.5 }}>
                {loading && stories.length === 0 && (
                    <Box sx={GRID_SX}>
                        {Array.from({ length: Math.min(filters.perPage, 12) }).map((_, index) => (
                            <Skeleton key={index} variant="rounded" animation="wave" sx={{ aspectRatio: '9 / 21', height: 'auto', width: '100%' }} />
                        ))}
                    </Box>
                )}
                {stories.length > 0 && (
                    <Box sx={{ ...GRID_SX, opacity: loading ? 0.5 : 1, transition: 'opacity .2s', pointerEvents: loading ? 'none' : 'auto' }}>
                        {stories.map((story) => (
                            <StoryCard
                                key={story.storyId}
                                story={story}
                                isPlaying={playingId === story.storyId}
                                onOpen={openModal}
                                onPlay={(item) => setPlayingId(item.storyId)}
                                onStop={() => setPlayingId(null)}
                            />
                        ))}
                    </Box>
                )}
                {!loading && stories.length === 0 && status !== 'idle' && (
                    <Panel>
                        <EmptyState title="No stories match these filters" hint={hasActiveFilters(filters) ? 'Try removing a filter or clearing all of them.' : undefined} />
                    </Panel>
                )}
            </Box>

            {total > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 3, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Per page</Typography>
                        <FormControl size="small">
                            <Select
                                value={filters.perPage}
                                disabled={loading}
                                onChange={(event) => updateFilters({ perPage: Number(event.target.value) })}
                                sx={{ minWidth: 76 }}
                            >
                                {PER_PAGE_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                    <Pagination
                        page={Math.min(filters.page, pageCount)}
                        count={pageCount}
                        color="primary"
                        disabled={loading}
                        onChange={(_event, page) => updateFilters({ page }, { keepPage: true })}
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            <StoryViewerModal
                story={openStory}
                open={modalOpen && openStory !== null}
                onClose={closeModal}
                index={openIndex ?? undefined}
                total={stories.length}
                onPrev={showPrev}
                onNext={showNext}
            />
        </PageContainer>
    );
};

export default StoryViewerPage;
